import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import tokenStorage from '../../../utils/tokenStorage';
import copyToClipboard from '../../../utils/clipboard';
import logoFull from '../../../assets/images/logo_aktivity.png';
import logoIcon from '../../../assets/images/standalone_logo.png';

/**
 * Format duration from minutes to hours and minutes
 */
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const PreSessionTab = ({ sessionId, participantId, readOnly = false, sessionStatus, hasPostSession, onTabChange, onBack, embedded = false, activeSection }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [assessmentDirty, setAssessmentDirty] = useState(false);
  const [assessmentSaved, setAssessmentSaved] = useState(false);
  const [challenges, setChallenges] = useState(['']);
  const [expectations, setExpectations] = useState(['']);
  const [activeNavItem, setActiveNavItem] = useState(activeSection || 'welcome');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Sync activeSection from parent (lobby) when it changes
  useEffect(() => {
    if (activeSection) setActiveNavItem(activeSection);
  }, [activeSection]);

  // Get participant info from storage
  const participant = tokenStorage.getParticipantData(sessionId);

  useEffect(() => {
    fetchPreSession();
  }, [sessionId]);

  const fetchPreSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/participant/sessions/${sessionId}/pre-session`);
      if (response.success) {
        setData(response.data);
        const progress = response.data.progress;
        if (progress.selfAssessmentResponses?.length > 0) {
          const answers = {};
          progress.selfAssessmentResponses.forEach(r => { answers[r.questionIndex] = r.response; });
          setAssessmentAnswers(answers);
          setAssessmentSaved(true);  // has stored responses → treat as saved
        } else {
          setAssessmentSaved(false);
        }
        setAssessmentDirty(false);
        if (progress.challenges?.length > 0) setChallenges(progress.challenges.map(c => c.text));
        if (progress.expectations?.length > 0) setExpectations(progress.expectations.map(e => e.text));
      }
    } catch (error) {
      console.error('Error fetching pre-session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialView = async (material) => {
    window.open(material.url, '_blank');
    if (readOnly) return;
    try {
      await api.post(`/participant/sessions/${sessionId}/pre-session/material-view`, {
        materialId: material._id || material.name,
        materialName: material.name,
      });
      fetchPreSession();
    } catch (error) {
      console.error('Error marking material viewed:', error);
    }
  };

  // Pull the most useful message out of an axios/api error so the toast
  // tells the participant *why* the save failed (e.g. "Participant not
  // found" vs "Self-assessment not enabled" vs network drop) instead of
  // a generic "Failed to save" that hides the cause.
  const extractError = (error, fallback) => {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.data?.error ||
      error?.message ||
      fallback
    );
  };

  // Detect "your participant id was lost" failures. Guest participants
  // (joined via QR/code, no user account) carry their participantId in
  // sessionStorage. Safari ITP, mobile memory pressure, or a closed tab
  // can wipe that, so the next save returns 403 "Participant not found".
  // When this happens we offer a one-tap rejoin instead of leaving the
  // user staring at a dead toast — the join page repopulates the
  // participantId for this session.
  const handleSaveError = (error, fallback) => {
    const msg = extractError(error, fallback);
    const status = error?.response?.status;
    const isLostParticipant =
      status === 403 ||
      /participant not found/i.test(String(msg));

    if (isLostParticipant) {
      const code = data?.session?.code;
      toast.error(
        (t) => (
          <div className="flex flex-col gap-2">
            <span className="font-medium">Your session was disconnected. Rejoin to save your answers.</span>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(t.id);
                navigate(code ? `/join/${code}` : '/join');
              }}
              className="self-start px-3 py-1 rounded-md bg-white text-red-600 text-xs font-semibold hover:bg-red-50"
            >
              Rejoin session
            </button>
          </div>
        ),
        { duration: 12000 }
      );
      return;
    }

    toast.error(msg);
  };

  const handleSaveAssessment = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const questions = data.config.selfAssessment?.questions || [];
      const responses = questions.map((q, idx) => ({
        questionIndex: idx, questionText: q.questionText, questionType: q.questionType, response: assessmentAnswers[idx] || '',
      }));
      await api.post(`/participant/sessions/${sessionId}/pre-session/self-assessment`, { responses });
      toast.success('Assessment saved!');
      setAssessmentSaved(true);
      setAssessmentDirty(false);
      fetchPreSession();
    } catch (error) {
      console.error('[PreSession] Save assessment failed:', error);
      handleSaveError(error, 'Failed to save assessment');
    }
    finally { setSaving(false); }
  };

  const handleSaveChallenges = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      await api.post(`/participant/sessions/${sessionId}/pre-session/challenges`, {
        challenges: challenges.filter(c => c.trim()),
      });
      toast.success('Challenges saved!');
      fetchPreSession();
    } catch (error) {
      console.error('[PreSession] Save challenges failed:', error);
      handleSaveError(error, 'Failed to save challenges');
    }
    finally { setSaving(false); }
  };

  const handleSaveExpectations = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      await api.post(`/participant/sessions/${sessionId}/pre-session/expectations`, {
        expectations: expectations.filter(e => e.trim()),
      });
      toast.success('Expectations saved!');
      fetchPreSession();
    } catch (error) {
      console.error('[PreSession] Save expectations failed:', error);
      handleSaveError(error, 'Failed to save expectations');
    }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex">
        <div className="hidden lg:flex flex-col w-64 border-r border-neutral-200 p-4">
          <div className="h-10 w-36 bg-neutral-100 rounded animate-pulse mb-8" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-neutral-100 rounded animate-pulse" />)}
          </div>
        </div>
        <div className="flex-1 p-6">
          <div className="h-8 w-64 bg-neutral-100 rounded animate-pulse" />
          <div className="h-4 w-48 bg-neutral-100 rounded animate-pulse mt-2" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {[1, 2, 3].map(i => <div key={i} className="h-28 bg-neutral-100 rounded-xl animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md text-center">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">Pre-Session Not Available</h3>
          <p className="text-neutral-600 mb-6">This session doesn't have pre-session tasks configured.</p>
          <button onClick={() => navigate('/join')} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600">Back to Join</button>
        </div>
      </div>
    );
  }

  const { config, progress, session: sessionInfo, participantCode, managerCode } = data;

  // Build navigation items based on what's enabled
  const baseNavigationItems = [
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => setActiveNavItem('welcome'),
    },
  ];

  if (config.materials?.length > 0) {
    baseNavigationItems.push({
      id: 'materials',
      name: 'Pre-Read Materials',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('materials'),
      badge: progress.materialViews?.length >= config.materials.length ? 'Done' : null,
    });
  }

  if (config.selfAssessment?.enabled && config.selfAssessment.questions?.length > 0) {
    baseNavigationItems.push({
      id: 'assessment',
      name: 'Self-Assessment',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('assessment'),
      badge: progress.selfAssessmentResponses?.length >= config.selfAssessment.questions.length ? 'Done' : null,
    });
  }

  if (config.challengeCapture?.enabled) {
    baseNavigationItems.push({
      id: 'challenges',
      name: 'Challenges',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('challenges'),
      badge: progress.challenges?.length > 0 ? 'Done' : null,
    });
  }

  if (config.expectationCapture?.enabled) {
    baseNavigationItems.push({
      id: 'expectations',
      name: 'Expectations',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('expectations'),
      badge: progress.expectations?.length > 0 ? 'Done' : null,
    });
  }

  const navigationItems = baseNavigationItems;

  const bottomNavItems = [];

  const formatSessionDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      + ' | ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // ==================== RENDER SECTIONS ====================

  /**
   * Read-only notice, shown above WHICHEVER section is active.
   *
   * Pre-session is a "before" snapshot, so it locks the moment the trainer starts the
   * session and stays locked once it completes — editing a baseline after being taught
   * the material would make the pre/post comparison meaningless.
   *
   * This used to live inside renderWelcome, but renderContent is a switch that renders
   * exactly one section: on Assessment, Challenges and Expectations the notice could
   * never appear. Participants got emoji buttons that silently did nothing and text
   * inputs that swallowed keystrokes, which reads as a broken page rather than a closed
   * one. Rendered here it covers every section.
   */
  const readOnlyNotice = readOnly ? (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
      <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
      <p className="text-sm text-amber-700">Pre-session is closed. Your submissions are shown below (read-only).</p>
    </div>
  ) : null;

  const renderWelcome = () => (
    <div>
      {/* Welcome Header */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">
                Welcome, {participant?.name || 'Participant'}!
              </h1>
              <p className="text-neutral-500 text-sm">Complete your pre-session preparation</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-blue-100 text-blue-700">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            Pre-Session
          </span>
        </div>

        {/* Session Info */}
        <div className="pt-4 border-t border-neutral-200">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Session</p>
              <h2 className="text-lg font-bold text-neutral-900 break-words">{sessionInfo?.name || 'Training Session'}</h2>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-neutral-400 uppercase tracking-wider">Code</p>
              <p className="text-xl font-mono font-bold text-neutral-900">#{sessionInfo?.code}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Participant Code */}
      {participantCode && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Your Participant Code</p>
                <p className="text-xl font-mono font-bold text-purple-700">{participantCode}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                const ok = await copyToClipboard(participantCode);
                if (ok) toast.success('Copied!');
                else toast.error('Could not copy — long-press the code to copy manually.');
              }}
              className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-purple-500 mt-2">Save this code to rejoin the session. Use it along with session code #{sessionInfo?.code}</p>
        </div>
      )}

      {/* Manager Code */}
      {managerCode && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-indigo-600 font-medium uppercase tracking-wider">Manager Code</p>
                <p className="text-xl font-mono font-bold text-indigo-700">{managerCode}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                const ok = await copyToClipboard(managerCode);
                if (ok) toast.success('Copied!');
                else toast.error('Could not copy — long-press the code to copy manually.');
              }}
              className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-indigo-500 mt-2">Share this code with your manager to track your post-session progress</p>
        </div>
      )}

      {/* Session Details Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
        {/* Trainer */}
        {sessionInfo?.trainerName && (
          <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Trainer</p>
                <p className="text-xs sm:text-sm font-semibold text-neutral-900 truncate">{sessionInfo.trainerName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Duration */}
        {sessionInfo?.duration > 0 && (
          <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Duration</p>
                <p className="text-xs sm:text-sm font-semibold text-neutral-900">{formatDuration(sessionInfo.duration)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Session Code */}
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Code</p>
              <p className="text-xs sm:text-sm font-mono font-semibold text-neutral-900">{sessionInfo?.code}</p>
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 overflow-hidden">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-[10px] sm:text-xs text-neutral-400 uppercase tracking-wider">Progress</p>
              <p className="text-xs sm:text-sm font-semibold text-neutral-900">{progress.completionPercent}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Session Date & Time */}
      {(sessionInfo?.startTime || sessionInfo?.scheduledDate) && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Session Date</p>
            <p className="text-sm font-semibold text-blue-900">{formatSessionDate(sessionInfo.startTime || sessionInfo.scheduledDate)}</p>
            {sessionInfo.endTime && (
              <p className="text-xs text-blue-700 mt-0.5">Ends: {formatSessionDate(sessionInfo.endTime)}</p>
            )}
          </div>
        </div>
      )}

      {/* Bottom Section - Progress & Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Progress Card */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Your Progress
          </h3>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-neutral-600">{progress.completedTasks} of {progress.totalTasks} completed</span>
              <span className="text-sm font-bold text-primary-600">{progress.completionPercent}%</span>
            </div>
            <div className="w-full bg-neutral-100 rounded-full h-2.5">
              <motion.div
                className="bg-primary-500 h-2.5 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress.completionPercent}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
          </div>
          <div className="space-y-2">
            {config.materials?.length > 0 && (
              <button onClick={() => setActiveNavItem('materials')} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-neutral-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${progress.materialViews?.length >= config.materials.length ? 'bg-green-100 text-green-600' : 'bg-neutral-200 text-neutral-400'}`}>
                    {progress.materialViews?.length >= config.materials.length ? '✓' : ''}
                  </span>
                  <span className="text-sm text-neutral-700">Pre-Read Materials</span>
                </div>
                <span className="text-xs text-neutral-400">{progress.materialViews?.length || 0}/{config.materials.length}</span>
              </button>
            )}
            {config.selfAssessment?.enabled && (
              <button onClick={() => setActiveNavItem('assessment')} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-neutral-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${progress.selfAssessmentResponses?.length >= (config.selfAssessment.questions?.length || 0) ? 'bg-green-100 text-green-600' : 'bg-neutral-200 text-neutral-400'}`}>
                    {progress.selfAssessmentResponses?.length >= (config.selfAssessment.questions?.length || 0) ? '✓' : ''}
                  </span>
                  <span className="text-sm text-neutral-700">Self-Assessment</span>
                </div>
              </button>
            )}
            {config.challengeCapture?.enabled && (
              <button onClick={() => setActiveNavItem('challenges')} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-neutral-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${progress.challenges?.length > 0 ? 'bg-green-100 text-green-600' : 'bg-neutral-200 text-neutral-400'}`}>
                    {progress.challenges?.length > 0 ? '✓' : ''}
                  </span>
                  <span className="text-sm text-neutral-700">Challenges</span>
                </div>
              </button>
            )}
            {config.expectationCapture?.enabled && (
              <button onClick={() => setActiveNavItem('expectations')} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-neutral-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${progress.expectations?.length > 0 ? 'bg-green-100 text-green-600' : 'bg-neutral-200 text-neutral-400'}`}>
                    {progress.expectations?.length > 0 ? '✓' : ''}
                  </span>
                  <span className="text-sm text-neutral-700">Expectations</span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Getting Started Tips */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-base font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Getting Started
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <div>
                <p className="text-sm font-medium text-neutral-900">Review Pre-Read Materials</p>
                <p className="text-xs text-neutral-500">Go through all training materials before the session</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <div>
                <p className="text-sm font-medium text-neutral-900">Complete Self-Assessment</p>
                <p className="text-xs text-neutral-500">Rate your current skills and knowledge</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
              <div>
                <p className="text-sm font-medium text-neutral-900">Share Challenges & Expectations</p>
                <p className="text-xs text-neutral-500">Help the trainer customize the session for you</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-100">
            <p className="text-xs text-neutral-400 text-center">
              Complete all tasks before the session begins for the best experience
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMaterials = () => (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Pre-Read Materials</h1>
        <p className="text-neutral-500 text-sm mt-1">Review these materials before the session begins</p>
      </div>
      <div className="space-y-3">
        {config.materials.map((material, idx) => {
          const viewed = progress.materialViews?.some(v => v.materialId === (material._id || material.name));
          return (
            <div key={idx} className={`bg-white rounded-xl border p-4 sm:p-5 flex items-center gap-4 transition-all ${viewed ? 'border-green-200' : 'border-neutral-200'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${viewed ? 'bg-green-100' : 'bg-neutral-100'}`}>
                {viewed ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-neutral-900 truncate">{material.name}</p>
                <p className={`text-xs mt-0.5 ${viewed ? 'text-green-600 font-medium' : 'text-neutral-500'}`}>
                  {viewed ? 'Viewed' : 'Not viewed yet'}
                </p>
              </div>
              <button
                onClick={() => handleMaterialView(material)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                  viewed ? 'text-green-700 bg-green-50 hover:bg-green-100 border border-green-200' : 'text-white bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {viewed ? 'View Again' : 'View'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAssessment = () => {
    const questions = config.selfAssessment?.questions || [];

    // Empty state: trainer/client enabled self-assessment but didn't add questions
    if (questions.length === 0) {
      return (
        <div>
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Self-Assessment</h1>
            <p className="text-neutral-500 text-sm mt-1">Rate yourself on the following areas</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
            <div className="w-14 h-14 mx-auto bg-neutral-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-neutral-900 mb-1">No self-assessment questions available</p>
            <p className="text-xs text-neutral-500">
              Your trainer hasn't added any questions for this section yet.
            </p>
          </div>
        </div>
      );
    }

    return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Self-Assessment</h1>
        <p className="text-neutral-500 text-sm mt-1">Rate yourself on the following areas</p>
      </div>
      <div className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
        <div className="space-y-6">
          {questions.map((q, idx) => (
            <div key={idx} className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{idx + 1}</span>
                <label className="text-sm font-medium text-neutral-800 leading-relaxed">{q.questionText}</label>
              </div>
              {q.questionType === 'rating' && (
                <div className="flex gap-2 sm:gap-3 ml-10">
                  {[
                    { value: 1, emoji: '😞', label: 'Poor' },
                    { value: 2, emoji: '😑', label: 'Fair' },
                    { value: 3, emoji: '🙂', label: 'Okay' },
                    { value: 4, emoji: '😄', label: 'Good' },
                    { value: 5, emoji: '🤩', label: 'Excellent' },
                  ].map(option => (
                    <button key={option.value} disabled={readOnly}
                      onClick={() => { if (!readOnly) { setAssessmentAnswers(prev => ({ ...prev, [idx]: option.value })); setAssessmentDirty(true); } }}
                      className={`flex flex-col items-center p-2 sm:p-3 rounded-xl border-2 transition-all ${
                        assessmentAnswers[idx] === option.value
                          ? 'border-primary-500 bg-primary-50 scale-105'
                          : 'border-transparent hover:border-neutral-300 bg-neutral-50'
                      } ${readOnly ? 'cursor-default opacity-70' : ''}`}
                    >
                      <span className="text-xl sm:text-2xl">{option.emoji}</span>
                      <span className="text-[9px] sm:text-xs text-neutral-500 mt-1">{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
              {q.questionType === 'text' && (
                <div className="ml-10">
                  <textarea value={assessmentAnswers[idx] || ''} readOnly={readOnly}
                    onChange={(e) => { if (!readOnly) { setAssessmentAnswers(prev => ({ ...prev, [idx]: e.target.value })); setAssessmentDirty(true); } }}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none" rows={3} placeholder="Type your answer..."
                  />
                </div>
              )}
              {q.questionType === 'mcq' && (
                <div className="ml-10 space-y-2">
                  {(q.options || []).map((opt, optIdx) => (
                    <label key={optIdx} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      assessmentAnswers[idx] === opt ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300'
                    } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                      <input type="radio" name={`q-${idx}`} checked={assessmentAnswers[idx] === opt} disabled={readOnly}
                        onChange={() => { if (!readOnly) { setAssessmentAnswers(prev => ({ ...prev, [idx]: opt })); setAssessmentDirty(true); } }}
                        className="text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-neutral-700">{opt}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          {!readOnly && (() => {
            const showSaved = assessmentSaved && !assessmentDirty;
            return (
              <button
                onClick={handleSaveAssessment}
                disabled={saving || showSaved}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
                  showSaved
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50'
                }`}
              >
                {saving ? 'Saving...' : showSaved ? '✓ Saved' : 'Save Assessment'}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
    );
  };

  const renderChallenges = () => {
    // "Saved" state: user has persisted challenges and the on-screen inputs haven't drifted
    // from what's on the server. Editing an input flips the button back to "Save Challenges".
    const savedList = (progress.challenges || []).map(c => (c.text || '').trim()).filter(Boolean);
    const currentList = (challenges || []).map(c => (c || '').trim()).filter(Boolean);
    const isSaved = savedList.length > 0
      && savedList.length === currentList.length
      && savedList.every((t, i) => t === currentList[i]);
    return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Your Challenges</h1>
        <p className="text-neutral-500 text-sm mt-1">What challenges are you currently facing related to this topic?</p>
      </div>
      <div className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
        <div className="space-y-3">
          {challenges.map((challenge, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="w-7 h-7 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">{idx + 1}</span>
              <input type="text" value={challenge} readOnly={readOnly}
                onChange={(e) => { if (readOnly) return; const u = [...challenges]; u[idx] = e.target.value; setChallenges(u); }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={`Describe challenge ${idx + 1}...`}
              />
              {!readOnly && challenges.length > 1 && (
                <button onClick={() => setChallenges(challenges.filter((_, i) => i !== idx))}
                  className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors mt-0.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <div className="space-y-3 pt-2">
              <button onClick={() => setChallenges([...challenges, ''])}
                className="flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add another challenge
              </button>
              <button onClick={handleSaveChallenges} disabled={saving || isSaved}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                  isSaved
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50'
                }`}
              >
                {saving ? (
                  'Saving...'
                ) : isSaved ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Saved
                  </>
                ) : (
                  'Save Challenges'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  const renderExpectations = () => {
    const savedList = (progress.expectations || []).map(e => (e.text || '').trim()).filter(Boolean);
    const currentList = (expectations || []).map(e => (e || '').trim()).filter(Boolean);
    const isSaved = savedList.length > 0
      && savedList.length === currentList.length
      && savedList.every((t, i) => t === currentList[i]);
    return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Your Expectations</h1>
        <p className="text-neutral-500 text-sm mt-1">What do you hope to gain from this training?</p>
      </div>
      <div className="bg-white rounded-xl border border-neutral-200 p-5 sm:p-6">
        <div className="space-y-3">
          {expectations.map((expectation, idx) => (
            <div key={idx} className="flex gap-3 items-start">
              <span className="w-7 h-7 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">{idx + 1}</span>
              <input type="text" value={expectation} readOnly={readOnly}
                onChange={(e) => { if (readOnly) return; const u = [...expectations]; u[idx] = e.target.value; setExpectations(u); }}
                className="flex-1 px-4 py-2.5 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={`Describe expectation ${idx + 1}...`}
              />
              {!readOnly && expectations.length > 1 && (
                <button onClick={() => setExpectations(expectations.filter((_, i) => i !== idx))}
                  className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors mt-0.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <div className="space-y-3 pt-2">
              <button onClick={() => setExpectations([...expectations, ''])}
                className="flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add another expectation
              </button>
              <button onClick={handleSaveExpectations} disabled={saving || isSaved}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                  isSaved
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50'
                }`}
              >
                {saving ? (
                  'Saving...'
                ) : isSaved ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Saved
                  </>
                ) : (
                  'Save Expectations'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  const renderContent = () => {
    const section = (() => {
      switch (activeNavItem) {
        case 'welcome': return renderWelcome();
        case 'materials': return renderMaterials();
        case 'assessment': return renderAssessment();
        case 'challenges': return renderChallenges();
        case 'expectations': return renderExpectations();
        default: return renderWelcome();
      }
    })();

    // The notice sits above every section, not just Welcome — a locked Assessment or
    // Expectations tab is otherwise indistinguishable from a broken one.
    return (
      <>
        {readOnlyNotice}
        {section}
      </>
    );
  };

  const getPageTitle = () => {
    const item = navigationItems.find(n => n.id === activeNavItem);
    return item?.name || 'Pre-Session';
  };

  // ==================== EMBEDDED MODE (inside lobby page - no sidebar/header) ====================
  if (embedded) {
    return (
      <div className="p-4 sm:p-6 bg-white">
        {renderContent()}
      </div>
    );
  }

  // ==================== FULL-SCREEN LAYOUT with sidebar (before session starts) ====================
  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Back Button */}
        {onBack && (
          <div className="px-3 pt-2">
            <button
              onClick={onBack}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-all text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {!sidebarCollapsed && <span>Back</span>}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    activeNavItem === item.id
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {activeNavItem === item.id && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${activeNavItem === item.id ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            {/* Mobile: Back button + Logo. The desktop sidebar already has a Back
                button, but mobile hides the sidebar and its bottom bar doesn't
                include Back — so we surface it in the mobile header. */}
            <div className="lg:hidden flex items-center gap-2">
              {onBack && (
                <button
                  onClick={onBack}
                  aria-label="Back"
                  className="p-2 -ml-2 rounded-lg text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
              )}
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>

            {/* Title (Desktop) */}
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">{getPageTitle()}</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {sessionInfo?.code && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                  <span className="text-xs text-neutral-500">Code:</span>
                  <span className="text-xs font-mono font-semibold text-neutral-900">#{sessionInfo.code}</span>
                </div>
              )}
              <button
                onClick={() => navigate('/join')}
                className="px-3 py-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                Leave
              </button>
            </div>
          </div>

        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 bg-white overflow-y-auto pb-40 lg:pb-6">
          {renderContent()}
          {/* Mobile-only spacer — ensures Save Challenges / Save Expectations
              buttons clear the fixed bottom nav on short devices. */}
          <div className="lg:hidden h-20" aria-hidden="true" />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                item.locked ? 'text-neutral-300' : activeNavItem === item.id ? '' : 'text-neutral-500'
              }`}
              style={activeNavItem === item.id && !item.locked ? { color: '#ff8a00' } : {}}
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'materials' ? 'Materials' : item.id === 'assessment' ? 'Assessment' : item.id === 'live-session' ? 'Live' : item.id === 'post-session' ? 'Post' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default PreSessionTab;
